import { Album } from '../types/album';

/**
 * Utility functions for extracting filter values from albums
 */

export function getUniqueDecades(albums: Album[]): string[] {
  const decades = new Set<string>();
  
  albums.forEach(album => {
    if (album.year && album.year > 0) {
      const decade = Math.floor(album.year / 10) * 10;
      decades.add(`${decade}s`);
    }
  });
  
  return Array.from(decades).sort();
}

export function getUniqueCountries(albums: Album[]): Array<{ code: string; name: string; count: number }> {
  const countryMap = new Map<string, { name: string; count: number }>();
  
  albums.forEach(album => {
    if (album.artistCountry && album.artistCountryName) {
      const existing = countryMap.get(album.artistCountry);
      countryMap.set(album.artistCountry, {
        name: album.artistCountryName,
        count: existing ? existing.count + 1 : 1
      });
    }
  });
  
  return Array.from(countryMap.entries())
    .map(([code, data]) => ({ code, name: data.name, count: data.count }))
    .sort((a, b) => b.count - a.count); // Sort by frequency
}

export function getUniqueLanguages(albums: Album[]): Array<{ code: string; name: string; count: number }> {
  const languageMap = new Map<string, { name: string; count: number }>();
  
  albums.forEach(album => {
    if (album.language && album.languageName) {
      const existing = languageMap.get(album.language);
      languageMap.set(album.language, {
        name: album.languageName,
        count: existing ? existing.count + 1 : 1
      });
    }
  });
  
  return Array.from(languageMap.entries())
    .map(([code, data]) => ({ code, name: data.name, count: data.count }))
    .sort((a, b) => b.count - a.count); // Sort by frequency
}

export function getYearRange(albums: Album[]): [number, number] {
  if (albums.length === 0) return [1950, new Date().getFullYear()];
  
  const validYears = albums
    .map(album => album.year)
    .filter(year => year && year > 0);
  
  if (validYears.length === 0) return [1950, new Date().getFullYear()];
  
  return [Math.min(...validYears), Math.max(...validYears)];
}

export function getUniqueArtists(albums: Album[]): string[] {
  const artists = new Set<string>();
  
  albums.forEach(album => {
    const albumArtists = album.artists || [album.artist];
    albumArtists.forEach(artist => artists.add(artist));
  });
  
  return Array.from(artists).sort();
}

export function getUniqueGenres(albums: Album[]): string[] {
  const genres = new Set<string>();
  
  albums.forEach(album => {
    album.genres.forEach(genre => genres.add(genre));
  });
  
  return Array.from(genres).sort();
}